import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Menu, Flex, Badge, Avatar, Typography, Modal, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from 'hooks/Languages/useLanguage';
import { useFeatureGate } from 'hooks/useFeatureGate';
import {
  CoffeeOutlined,
  MedicineBoxOutlined,
  ShopOutlined,
  UserOutlined,
  CrownOutlined,
  ToolOutlined,
  TeamOutlined,
  DollarOutlined,
  SnippetsOutlined,
  GlobalOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  HomeOutlined,
  ScissorOutlined,
  CarOutlined,
  CameraOutlined,
  SkinOutlined,
  ScheduleOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import {
  getOverviewMenuItems,
  getPharmacyMenuItems,
  getRetailMenuItems,
  getInventoryMenuItems,
  getCustomerMenuItems,
  getFinanceAdminMenuItems,
  getAnalyticsMenuItems,
  getProcurementMenuItems,
  getSuperAdminMenuItems,
  getHospitalityMenuItems,
  getHospitalMenuItems,
  getHRMMenuItems,
  getShoeMenuItems,
  getClothingMenuItems,
  getGroceryMenuItems,
  getJewelryMenuItems,
  getHardwareMenuItems,
  getMobileMoneyMenuItems,
  getHotelMenuItems,
  getCoffeeShopMenuItems,
  getWholesaleMenuItems,
  getDistributorMenuItems,
  getRealEstateMenuItems,
  getBarbershopMenuItems,
  getSpaMenuItems,
  getTailoringMenuItems,
  getProfessionalServicesMenuItems,
  getSalonMenuItems,
  getCarRentalMenuItems,
  type MenuItem,
} from './menuConfig';
import { FEATURE_KEYS } from 'utils/featureConstants';
import { getRoleAllowedFeatures, isRestrictedRole } from 'utils/roleConfig';
import urls from 'routes/urls';
import { usePermissions } from 'hooks/usePermissions';

const KEY_TO_FEATURE_CODE: Record<string, string> = {
  // Paths
  '/settings/branches': 'multi_branch',
  '/settings/kitchen-display': 'kitchen_display',
  '/orders': 'table_orders',
  '/tables': 'table_orders',
  '/reports': 'advanced_reports',
  '/settings/reports': 'advanced_reports',
  '/customers': 'loyalty',
  '/expiry-alerts': 'expiry_alerts',
  '/settings/warehouse': 'advanced_inventory',
  '/stock-transfer': 'advanced_inventory',
  '/settings/developer': 'api_access',
  '/settings/api-integrations': 'api_access',
  '/settings/hrm': 'api_access',
  '/inpatient': 'inpatient_ipd',
  '/settings/ward-management': 'inpatient_ipd',

  // Keys
  'branches': 'multi_branch',
  'kitchen_display': 'kitchen_display',
  'kitchen_display_group': 'kitchen_display',
  'orders': 'table_orders',
  'tables': 'table_orders',
  'analytics': 'advanced_reports',
  'reports': 'advanced_reports',
  'sales_analytics': 'advanced_reports',
  'customers': 'loyalty',
  'customer_loyalty': 'loyalty',
  'expiry_alerts': 'expiry_alerts',
  'warehouse': 'advanced_inventory',
  'stock-transfer': 'advanced_inventory',
  'api-integrations': 'api_access',
  'inpatient': 'inpatient_ipd',
  'ward_management': 'inpatient_ipd',
};

const { Text } = Typography;

type AntMenuItem = Required<MenuProps>['items'][number];

interface Props {
  loadLogoutUser: boolean;
  onHandleLogout: () => void;
  userData: any;
  collapsed?: boolean;
}

// Role-based icons for the sidebar badge
const getRoleIcon = (role: string, businessType: string) => {
  const r = role.toLowerCase();
  if (r.includes('manager') || r.includes('administrator') || r.includes('admin')) return <CrownOutlined />;
  if (r.includes('waiter') || r.includes('server') || r.includes('host')) return <TeamOutlined />;
  if (r.includes('bartender') || r.includes('bar')) return <CoffeeOutlined />;
  if (r.includes('kitchen') || r.includes('chef')) return <ToolOutlined />;
  if (r.includes('pharmacist') || r.includes('technician')) return <MedicineBoxOutlined />;
  if (r.includes('cashier')) return <ShopOutlined />;
  if (r.includes('stock') || r.includes('inventory')) return <ShopOutlined />;
  if (r.includes('reception') || r.includes('concierge') || r.includes('front desk') || r.includes('housekeep') || r.includes('hotel')) return <HomeOutlined />;
  return <UserOutlined />;
};

// Role badge color by role category
const getRoleBadgeColor = (role: string): string => {
  const r = role.toLowerCase();
  if (r.includes('manager') || r.includes('administrator') || r.includes('admin')) return '#8b5cf6'; // purple
  if (r.includes('waiter') || r.includes('server')) return '#0ea5e9'; // sky blue
  if (r.includes('host')) return '#06b6d4'; // cyan
  if (r.includes('bartender') || r.includes('bar')) return '#f59e0b'; // amber
  if (r.includes('kitchen') || r.includes('chef')) return '#ef4444'; // red
  if (r.includes('pharmacist')) return '#10b981'; // emerald
  if (r.includes('technician')) return '#14b8a6'; // teal
  if (r.includes('cashier')) return '#3b82f6'; // blue
  if (r.includes('stock') || r.includes('inventory')) return '#6366f1'; // indigo
  return '#6b7280'; // gray
};

const MenuItems: React.FC<Props> = ({
  loadLogoutUser,
  onHandleLogout,
  userData,
  collapsed = false,
}) => {
  const { translate } = useLanguage();
  const { hasFeature } = useFeatureGate();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    hasAccess,
    hasPermission,
    isAdmin,
    businessType,
    rawBusinessType,
    userRole,
    userRolesList,
    roleAllowedFeatures,
  } = usePermissions();

  const hasHRMPermission = useMemo(() => {
    return hasPermission(FEATURE_KEYS.HRM);
  }, [hasPermission]);

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // Build the menu groups based on business type
  const menuItems = useMemo(() => {
    const isSuperAdmin =
      userData?.is_super_admin ??
      localStorage.getItem('isSuperAdmin') === 'true';

    if (isSuperAdmin) {
      return [
        {
          type: 'group',
          label: translate('PLATFORM CONTROL'),
          children: getSuperAdminMenuItems(translate),
          visible: true,
        }
      ];
    }

    const platformGroup: any[] = [];
    const overview = getOverviewMenuItems(translate, businessType, hasPermission);

    // Role badge chips (shown when user has specific restricted roles)
    const rolesList = userRolesList && userRolesList.length > 0
      ? userRolesList
      : (userRole && userRole !== 'unknown' ? [userRole] : []);

    const roleBadge = rolesList.length > 0 && !isAdmin ? [
      {
        type: 'group',
        label: (
          <Flex vertical gap={6} style={{ padding: '4px 0', marginBottom: 4 }}>
            {rolesList.map((role: string) => {
              const color = getRoleBadgeColor(role);
              return (
                <span key={role} style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: `${color}22`,
                  border: `1px solid ${color}55`,
                  borderRadius: 20,
                  padding: '3px 10px 3px 8px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  width: 'fit-content',
                }}>
                  <span style={{ fontSize: 12, lineHeight: 1 }}>
                    {getRoleIcon(role, businessType)}
                  </span>
                  {role}
                </span>
              );
            })}
          </Flex>
        ),
        children: [],
        visible: true,
      }
    ] : [];

    // ── FUKKONI Lite Merchant — Dedicated streamlined sidebar ─────────────────
    const planType = (
      userData?.shop?.plan_type ||
      userData?.plan_type ||
      localStorage.getItem('plan_type') ||
      ''
    ).toLowerCase();

    const isPureLite = planType === 'lite';

    const isHotelBusiness =
      businessType === 'hotel' ||
      (businessType as string) === 'hotel & hospitality' ||
      (businessType as string) === 'hotel_hospitality' ||
      (businessType as string) === 'hospitality' ||
      (typeof businessType === 'string' && (
        businessType.toLowerCase().includes('hotel') ||
        businessType.toLowerCase().includes('hostel') ||
        businessType.toLowerCase().includes('hospitality') ||
        businessType.toLowerCase().includes('lodge') ||
        businessType.toLowerCase().includes('resort') ||
        businessType.toLowerCase().includes('motel') ||
        businessType.toLowerCase().includes('guesthouse') ||
        businessType.toLowerCase().includes('guest_house') ||
        businessType.toLowerCase().includes('auberge') ||
        businessType.toLowerCase().includes('inn') ||
        businessType.toLowerCase().includes('hebergement') ||
        businessType.toLowerCase().includes('hébergement')
      )) ||
      (typeof rawBusinessType === 'string' && (
        rawBusinessType.toLowerCase().includes('hotel') ||
        rawBusinessType.toLowerCase().includes('hostel') ||
        rawBusinessType.toLowerCase().includes('hospitality') ||
        rawBusinessType.toLowerCase().includes('lodge') ||
        rawBusinessType.toLowerCase().includes('resort') ||
        rawBusinessType.toLowerCase().includes('motel') ||
        rawBusinessType.toLowerCase().includes('guesthouse') ||
        rawBusinessType.toLowerCase().includes('guest_house') ||
        rawBusinessType.toLowerCase().includes('auberge') ||
        rawBusinessType.toLowerCase().includes('inn') ||
        rawBusinessType.toLowerCase().includes('hebergement') ||
        rawBusinessType.toLowerCase().includes('hébergement')
      )) ||
      location.pathname.startsWith('/hotel');

    if (isPureLite) {
      // Hotel & Hospitality PMS (Lite)
      if (isHotelBusiness) {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('HOTEL & HOSPITALITY PMS'),
            children: getHotelMenuItems(translate),
            visible: true,
          },
          {
            type: 'group',
            label: translate('EXPENSES & FINANCE'),
            children: [
              {
                key: 'hotel_expenses',
                label: translate('Hotel Expenses'),
                icon: <DollarOutlined />,
                path: urls.expenses,
                featureKey: FEATURE_KEYS.EXPENSES,
              },
              {
                key: 'hotel_sales_reports',
                label: translate('Sales & Revenue Reports'),
                icon: <LineChartOutlined />,
                path: urls.reports,
                featureKey: FEATURE_KEYS.REPORTS,
              },
            ],
            visible: true,
          },
          {
            type: 'group',
            label: translate('STAFF & PAYROLL'),
            children: getHRMMenuItems(translate),
            visible: isAdmin || hasHRMPermission,
          },
          {
            type: 'group',
            label: translate('ADMINISTRATION'),
            children: getFinanceAdminMenuItems(translate, businessType).filter(
              (item) => item.key !== 'website-management'
            ),
            visible: isAdmin,
          },
        ].filter((group) => group.visible);
      }

      // 1. Hair Salon & Beauty Studio
      if (businessType === 'salon') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('SALON & BEAUTY STUDIO'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Online Booking & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Services & Treatment Menu'),
                icon: <ScissorOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Appointments & Bookings'),
                icon: <CalendarOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'salon_clients',
                label: translate('Clients Directory'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Salon & Store Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 2. Barbershop & Grooming
      if (businessType === 'barbershop') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('BARBERSHOP & GROOMING'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Online Booking & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Grooming & Haircut Menu'),
                icon: <ScissorOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Appointments & Bookings'),
                icon: <CalendarOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'barber_clients',
                label: translate('Clients Directory'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Barbershop Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 3. Real Estate & Property
      if (businessType === 'real_estate') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('REAL ESTATE & PROPERTIES'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Property Showcase & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Property Listings & Spaces'),
                icon: <HomeOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Inquiries & Viewing Leads'),
                icon: <ScheduleOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'property_clients',
                label: translate('Client Leads Directory'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Showcase Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 4. Spa & Wellness Studio
      if (businessType === 'spa') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('SPA & WELLNESS STUDIO'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Online Booking & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Spa Treatments & Packages'),
                icon: <ScheduleOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Appointments & Sessions'),
                icon: <CalendarOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'spa_clients',
                label: translate('Clients Directory'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Spa Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 5. Tailoring & Custom Couture
      if (businessType === 'tailoring') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('TAILORING & COUTURE'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Portfolio & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Creations & Fitting Menu'),
                icon: <SkinOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Fitting Requests & Orders'),
                icon: <CalendarOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'tailor_clients',
                label: translate('Clients & Measurements'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Atelier Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 6. Car Rental & Vehicle Hire
      if (businessType === 'car_rental') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('CAR RENTAL & FLEET'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Fleet Showcase & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Vehicle Fleet & Rates'),
                icon: <CarOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Rental Reservations'),
                icon: <CalendarOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'rental_clients',
                label: translate('Rental Clients'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Fleet Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 7. Photography & Media Studio
      if (businessType === 'photography') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('PHOTOGRAPHY STUDIO'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Portfolio & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Session Packages & Rates'),
                icon: <CameraOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Shoot Bookings & Requests'),
                icon: <CalendarOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'photo_clients',
                label: translate('Clients Directory'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Studio Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 8. Freelancer & Consulting Services
      if (businessType === 'freelancer') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('PROFESSIONAL SERVICES'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Bio Link & Services Page'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Services & Consultation Packages'),
                icon: <ScheduleOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Consultation Bookings'),
                icon: <CalendarOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'freelance_clients',
                label: translate('Client Directory'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Service Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 9. Restaurant / Café (Lite)
      if (businessType === 'restaurant') {
        return [
          ...roleBadge,
          {
            type: 'group',
            label: translate('ONLINE MENU & FOOD ORDERS'),
            children: [
              {
                key: 'my_online_store',
                label: translate('Digital Menu & Bio Link'),
                icon: <GlobalOutlined />,
                path: urls.merchantOnlineStore,
              },
              {
                key: 'storefront_products',
                label: translate('Menu Dishes & Drinks'),
                icon: <CoffeeOutlined />,
                path: urls.storefrontProducts,
              },
              {
                key: 'storefront_orders',
                label: translate('Food Orders & Deliveries'),
                icon: <ShoppingCartOutlined />,
                path: urls.storefrontOrders,
              },
              {
                key: 'food_clients',
                label: translate('Customers Directory'),
                icon: <TeamOutlined />,
                path: urls.customers,
              },
              {
                key: 'storefront_settings',
                label: translate('Menu Settings'),
                icon: <ToolOutlined />,
                path: urls.storefrontSettings,
              },
            ],
            visible: true,
          },
        ];
      }

      // 10. General Retail / Boutique / Shop (Default Lite)
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('FUKKONI LITE STORE'),
          children: [
            {
              key: 'my_online_store',
              label: translate('Online Store & Bio Link'),
              icon: <GlobalOutlined />,
              path: urls.merchantOnlineStore,
            },
            {
              key: 'storefront_products',
              label: translate('Catalog & Products'),
              icon: <ShopOutlined />,
              path: urls.storefrontProducts,
            },
            {
              key: 'storefront_orders',
              label: translate('Online & WhatsApp Orders'),
              icon: <ShoppingCartOutlined />,
              path: urls.storefrontOrders,
            },
            {
              key: 'retail_clients',
              label: translate('Customers Directory'),
              icon: <TeamOutlined />,
              path: urls.customers,
            },
            {
              key: 'storefront_settings',
              label: translate('Storefront Settings'),
              icon: <ToolOutlined />,
              path: urls.storefrontSettings,
            },
          ],
          visible: true,
        },
      ];
    }

    // ── Hotel & Hospitality PMS — dedicated structured sidebar ───────────────────
    if (isHotelBusiness) {
      return [
        ...platformGroup,
        ...roleBadge,
        {
          type: 'group',
          label: translate('HOTEL & HOSPITALITY PMS'),
          children: getHotelMenuItems(translate),
          visible: true,
        },
        {
          type: 'group',
          label: translate('EXPENSES & FINANCE'),
          children: [
            {
              key: 'hotel_expenses',
              label: translate('Hotel Expenses'),
              icon: <DollarOutlined />,
              path: urls.expenses,
              featureKey: FEATURE_KEYS.EXPENSES,
            },
            {
              key: 'hotel_sales_reports',
              label: translate('Sales & Revenue Reports'),
              icon: <LineChartOutlined />,
              path: urls.reports,
              featureKey: FEATURE_KEYS.REPORTS,
            },
          ],
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(
            (item) => item.key !== 'website-management'
          ),
          visible: isAdmin,
        },
      ].filter((group) => group.visible);
    }

    // ── Mobile Money & Agency Banking — dedicated structured sidebar ──────────────
    if (
      businessType === 'mobile_money' ||
      businessType.includes('mobile_money') ||
      businessType.includes('mobile money') ||
      businessType.includes('agency_banking') ||
      businessType.includes('agency banking') ||
      businessType.includes('momo') ||
      businessType.includes('financier') ||
      businessType.includes('financial')
    ) {
      const momoItems = getMobileMoneyMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('1. FLOAT & TILL MANAGEMENT'),
          children: momoItems.filter(i => ['momo_dashboard', 'momo_floats'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('2. AGENT POS & REGISTER'),
          children: momoItems.filter(i => ['momo_pos', 'momo_ledger', 'momo_customer_history'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('3. DEBTS & RECONCILIATION'),
          children: momoItems.filter(i => ['momo_debts', 'momo_reconciliation'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('4. EXPENSES & FINANCE'),
          children: [
            {
              key: 'momo_expenses',
              label: translate('Expenses & Outflows'),
              icon: <DollarOutlined />,
              path: urls.expenses,
              featureKey: FEATURE_KEYS.EXPENSES,
            },
            {
              key: 'momo_reports',
              label: translate('Financial Analytics'),
              icon: <SnippetsOutlined />,
              path: urls.reports,
              featureKey: FEATURE_KEYS.REPORTS,
            },
          ],
          visible: true,
        },
        {
          type: 'group',
          label: translate('5. STAFF & ADMINISTRATION'),
          children: [
            ...getHRMMenuItems(translate),
            ...getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management')
          ],
          visible: isAdmin || hasHRMPermission,
        },
      ].filter(group => group.visible);
    }

    // ── Hardware Store — dedicated sidebar ──────────────────────────────────────
    if (businessType === 'hardware') {
      const hwItems = getHardwareMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('HARDWARE OPERATIONS'),
          children: hwItems,
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── Shoe Store — dedicated sidebar ──────────────────────────────────────
    if (businessType === 'shoe') {
      const shoeItems = getShoeMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('SALES & POS'),
          children: shoeItems.filter(i => ['shoe_pos', 'shoe_sales_history', 'shoe_prepacks'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('INVENTORY & SUPPLIERS'),
          children: shoeItems.filter(i => ['shoe_suppliers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('FLOOR & DISPLAY'),
          children: shoeItems.filter(i => ['shoe_audits', 'shoe_mismatched'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('SERVICES & TOOLS'),
          children: shoeItems.filter(i => ['shoe_care', 'shoe_labels', 'shoe_size_converter'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('CUSTOMERS & CREDIT'),
          children: shoeItems.filter(i => ['shoe_customers', 'shoe_debts'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('ANALYTICS & REPORTS'),
          children: shoeItems.filter(i => ['shoe_sales_analytics', 'shoe_reports', 'shoe_audit_logs'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── Grocery Supermarket — dedicated sidebar ─────────────────────────────
    if (businessType === 'grocery') {
      const groceryItems = getGroceryMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('SALES & SCALE POS'),
          children: groceryItems.filter(i => ['grocery_pos', 'grocery_sales_history'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('PRODUCE & INVENTORY'),
          children: groceryItems.filter(i => ['grocery_inventory', 'grocery_expiries', 'grocery_spoilage', 'grocery_suppliers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('CUSTOMERS'),
          children: groceryItems.filter(i => ['grocery_customers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('ANALYTICS & REPORTS'),
          children: groceryItems.filter(i => ['grocery_reports', 'grocery_audit_logs'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── Jewelry Store — dedicated sidebar ───────────────────────────────────
    if (businessType === 'jewelry') {
      const jewelryItems = getJewelryMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('SALES & POS'),
          children: jewelryItems.filter(i => ['jewelry_pos', 'jewelry_sales_history'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('VAULT & INVENTORY'),
          children: jewelryItems.filter(i => ['jewelry_vault', 'jewelry_certificates', 'jewelry_suppliers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('PRECIOUS METALS'),
          children: jewelryItems.filter(i => ['jewelry_spot_rates', 'jewelry_trade_ins'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('CUSTOMERS'),
          children: jewelryItems.filter(i => ['jewelry_customers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('ANALYTICS & REPORTS'),
          children: jewelryItems.filter(i => ['jewelry_reports', 'jewelry_audit_logs'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── Fashion & Apparel Store — dedicated sidebar ─────────────────────────
    if (businessType === 'clothing') {
      const fashionItems = getClothingMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('BOUTIQUE & POS'),
          children: fashionItems.filter(i => ['clothing_pos', 'clothing_sales_history'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('APPAREL & STYLES'),
          children: fashionItems.filter(i => ['clothing_matrix', 'clothing_stock'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('FITTING & ALTERATIONS'),
          children: fashionItems.filter(i => ['clothing_fitting', 'clothing_alterations'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('SUPPLIERS & BRANDS'),
          children: fashionItems.filter(i => ['clothing_suppliers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('CLIENTS & STORE CREDIT'),
          children: fashionItems.filter(i => ['clothing_customers', 'clothing_debts'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('ANALYTICS & REPORTS'),
          children: fashionItems.filter(i => ['clothing_analytics', 'clothing_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── Coffee Shop & Roaster — dedicated sidebar ─────────────────────────
    if (businessType === 'coffee_shop') {
      const coffeeItems = getCoffeeShopMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('BARISTA & ORDERS'),
          children: coffeeItems.filter(i => ['coffee_pos', 'coffee_orders', 'coffee_loyalty'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('DRINKS & RECIPES'),
          children: coffeeItems.filter(i => ['coffee_recipes', 'coffee_modifiers', 'coffee_spillage'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('SHIFTS & STOCK'),
          children: coffeeItems.filter(i => ['coffee_shifts', 'coffee_stock', 'coffee_movements', 'coffee_suppliers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('CUSTOMERS'),
          children: coffeeItems.filter(i => ['coffee_customers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('ANALYTICS & REPORTS'),
          children: coffeeItems.filter(i => ['coffee_reports', 'coffee_audit_logs'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── Wholesaler & FMCG B2B — dedicated sidebar ───────────────────────────
    if (businessType === 'wholesale') {
      const wholesaleItems = getWholesaleMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('B2B WORKSTATION & ORDERS'),
          children: wholesaleItems.filter(i => ['wholesale_pos', 'wholesale_sales_history', 'wholesale_clients', 'wholesale_debts'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('WAREHOUSE & INVENTORY'),
          children: wholesaleItems.filter(i => ['wholesale_catalog', 'wholesale_stock', 'wholesale_movements'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('PROCUREMENT & LOGISTICS'),
          children: wholesaleItems.filter(i => ['wholesale_purchase_orders', 'wholesale_delivery_notes', 'wholesale_suppliers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('ANALYTICS & REPORTS'),
          children: wholesaleItems.filter(i => ['wholesale_reports', 'wholesale_audit_logs'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── 1. Real Estate & Property Management — dedicated sidebar ───────────
    if (businessType === 'real_estate') {
      const reItems = getRealEstateMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('PORTFOLIO COMMAND'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('PROPERTIES & TENANCY'),
          children: reItems.filter(i => ['re_properties', 're_units', 're_rent_payments'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('FACILITIES & TENANTS'),
          children: reItems.filter(i => ['re_maintenance', 're_tenants', 're_arrears', 're_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── 2. Barbershop & Grooming — dedicated sidebar ───────────────────────
    if (businessType === 'barbershop') {
      const bbItems = getBarbershopMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('CHAIRS & WALK-IN QUEUE'),
          children: bbItems.filter(i => ['barber_pos', 'barber_chairs', 'barber_services'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('RETAIL & CLIENTELE'),
          children: bbItems.filter(i => ['barber_products', 'barber_clients', 'barber_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── 3. Spa & Wellness Sanctuary — dedicated sidebar ────────────────────
    if (businessType === 'spa') {
      const spaItems = getSpaMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('SANCTUARY OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('TREATMENTS & SUITES'),
          children: spaItems.filter(i => ['spa_bookings', 'spa_rooms', 'spa_treatments', 'spa_memberships'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('APOTHECARY & GUESTS'),
          children: spaItems.filter(i => ['spa_oils_stock', 'spa_guests', 'spa_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── 4. Tailoring & Custom Couture — dedicated sidebar ──────────────────
    if (businessType === 'tailoring') {
      const tlItems = getTailoringMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('ATELIER OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('BESPOKE PRODUCTION'),
          children: tlItems.filter(i => ['tailor_orders', 'tailor_measurements', 'tailor_fabrics'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('CLIENTELE & ACCOUNTS'),
          children: tlItems.filter(i => ['tailor_clients', 'tailor_balances', 'tailor_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── 5. Professional Services & Consulting — dedicated sidebar ──────────
    if (businessType === 'freelancer') {
      const psItems = getProfessionalServicesMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('PRACTICE OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('ENGAGEMENTS & BILLING'),
          children: psItems.filter(i => ['ps_projects', 'ps_timesheets', 'ps_retainers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('CORPORATE CRM & WIP'),
          children: psItems.filter(i => ['ps_clients', 'ps_receivables', 'ps_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── 6. Hair Salon & Beauty Studio — dedicated sidebar ──────────────────
    if (businessType === 'salon') {
      const slItems = getSalonMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('STUDIO OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('CHAIRS, COLOR & GLAM'),
          children: slItems.filter(i => ['salon_appointments', 'salon_stations', 'salon_services', 'salon_formulas'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('RETAIL & CLIENTELE'),
          children: slItems.filter(i => ['salon_retail', 'salon_clients', 'salon_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── 7. Car Rental & Fleet Operations — dedicated sidebar ───────────────
    if (businessType === 'car_rental') {
      const crItems = getCarRentalMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('FLEET COMMAND'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('DISPATCH & INSPECTIONS'),
          children: crItems.filter(i => ['cr_vehicles', 'cr_agreements', 'cr_inspections'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('HIRERS & ANALYTICS'),
          children: crItems.filter(i => ['cr_clients', 'cr_deposits', 'cr_reports'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // ── Regional Distributor & Supply Logistics — dedicated sidebar ─────────
    if (businessType === 'distributor') {
      const distributorItems = getDistributorMenuItems(translate);
      return [
        ...roleBadge,
        {
          type: 'group',
          label: translate('LOGISTICS & FLEET'),
          children: distributorItems.filter(i => ['distributor_delivery_notes', 'distributor_purchase_orders', 'distributor_suppliers'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('DISTRIBUTION SALES'),
          children: distributorItems.filter(i => ['distributor_workstation', 'distributor_invoices', 'distributor_clients', 'distributor_credit'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('DEPOT STORAGE & STOCK'),
          children: distributorItems.filter(i => ['distributor_stock', 'distributor_catalog', 'distributor_movements'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('ANALYTICS & COMPLIANCE'),
          children: distributorItems.filter(i => ['distributor_reports', 'distributor_audit_logs'].includes(i.key)),
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: getFinanceAdminMenuItems(translate, businessType).filter(item => item.key !== 'website-management'),
          visible: isAdmin,
        },
      ].filter(group => group.visible);
    }

    // Business-type operations block
    let operations: MenuItem[] = [];
    let pharmacyOperations: MenuItem[] = [];

    if (businessType === 'pharmacy') {
      operations = getPharmacyMenuItems(translate, businessType).filter(item => item.key !== 'pharmacy_dashboard');
    } else if (businessType === 'hospital') {
      operations = getHospitalMenuItems(translate);
      pharmacyOperations = getPharmacyMenuItems(translate, businessType).map(item => {
        if (item.key === 'pharmacy_dispensing_group' && item.children) {
          return {
            ...item,
            children: item.children.filter(child => child.key !== 'patients'),
          };
        }
        return item;
      });
    } else if (businessType === 'bar' || businessType === 'restaurant') {
      operations = getHospitalityMenuItems(translate, businessType);
    } else {
      // Retail, Fashion, Clothing, Grocery, Jewelry, General — show Point of Sale and Retail Operations
      operations = getRetailMenuItems(translate, businessType);
    }

    // For retail, inventory/procurement are folded into operations; skip separate groups
    const isRetailType =
      businessType === 'retail' ||
      businessType === 'general';
    const isHospitality = businessType === 'bar' || businessType === 'restaurant';
    const isPharmacy = businessType === 'pharmacy';
    const isMedical = businessType === 'hospital' || businessType === 'pharmacy';

    // Inventory & procurement groups (inventory/procurement are hidden for pharmacy & hospital as they use the dedicated pharmacy module)
    const inventory = (!isRetailType && !isMedical)
      ? getInventoryMenuItems(translate, businessType)
      : [];
    const procurement = (!isRetailType && !isMedical)
      ? getProcurementMenuItems(translate, businessType)
      : [];

    const consumers = getCustomerMenuItems(translate, businessType);

    // Analytics group (only for managers/admins — restrict for operational roles)
    const analytics = getAnalyticsMenuItems(translate, businessType);

    let financeAdmin = getFinanceAdminMenuItems(translate, businessType);
    if (!isSuperAdmin) {
      financeAdmin = financeAdmin.filter(item => item.key !== 'website-management');
    }

    let baseGroups = [];

    if (businessType === 'hospital') {
      baseGroups = [
        ...platformGroup,
        ...roleBadge,
        {
          type: 'group',
          label: translate('HMS COMMAND CENTER'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: translate('CLINICAL CARE (EMR)'),
          children: operations.filter(item => 
            ['hospital_appointments', 'patient_queue', 'hospital_triage', 'hospital_ambulance', 'hospital_consultation', 'hospital_patients', 'hospital_wards'].includes(item.key)
          ),
          visible: true,
        },
        {
          type: 'group',
          label: translate('DIAGNOSTICS & LABS'),
          children: operations.filter(item => 
            ['hospital_labs', 'hospital_pacs'].includes(item.key)
          ),
          visible: true,
        },
        {
          type: 'group',
          label: translate('HOSPITAL PHARMACY'),
          children: pharmacyOperations,
          visible: true,
        },
        {
          type: 'group',
          label: translate('BILLING & REVENUE'),
          children: [
            ...operations.filter(item => ['hospital_finance', 'hospital_reports'].includes(item.key)),
            ...analytics.flatMap(item => {
              if (item.key === 'analytics' && item.children) {
                const financial = item.children.find(child => child.key === 'financial_analytics');
                return financial ? [financial] : [];
              }
              return [];
            })
          ],
          visible: true,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: [
            ...financeAdmin,
            ...operations.filter(item => item.key === 'hospital_settings'),
          ],
          visible: isAdmin,
        },
      ];
    } else {
      baseGroups = [
        ...platformGroup,
        ...roleBadge,
        {
          type: 'group',
          label: translate('OVERVIEW'),
          children: overview,
          visible: true,
        },
        {
          type: 'group',
          label: isPharmacy ? translate('PHARMACY') :
                 isHospitality ? (
                   businessType === 'restaurant' ? translate('RESTAURANT') : translate('BAR')
                 ) :
                 isRetailType ? translate('SHOP') :
                 translate('OPERATIONS'),
          children: operations,
          visible: true,
        },
        {
          type: 'group',
          label: translate('PHARMACY'),
          children: pharmacyOperations,
          visible: false,
        },
        ...(!isRetailType ? [{
          type: 'group',
          label: translate('INVENTORY'),
          children: [...inventory, ...procurement],
          visible: true,
        }] : []),
        {
          type: 'group',
          label: translate('CUSTOMERS'),
          children: consumers,
          visible: !isMedical,
        },
        {
          type: 'group',
          label: translate('STAFF & PAYROLL'),
          children: getHRMMenuItems(translate),
          visible: isAdmin || hasHRMPermission,
        },
        {
          type: 'group',
          label: translate('ANALYTICS'),
          children: analytics,
          visible: !roleAllowedFeatures || roleAllowedFeatures.has(FEATURE_KEYS.SALES) || isAdmin,
        },
        {
          type: 'group',
          label: translate('ADMINISTRATION'),
          children: financeAdmin,
          visible: isAdmin,
        },
      ];
    }

    return baseGroups.filter(group => group.visible);
  }, [translate, userData, businessType, isAdmin, hasHRMPermission, userRole, roleAllowedFeatures]);

  // Convert to Ant Design Menu format
  const convertToAntdItems = useCallback(
    (items: MenuItem[]): AntMenuItem[] => {
      const getLabel = (item: MenuItem) => {
        const featureCode = KEY_TO_FEATURE_CODE[item.key] || KEY_TO_FEATURE_CODE[item.path || ''];
        const isLocked = featureCode && !hasFeature(featureCode as any);
        const textLabel = typeof item.label === 'string' ? item.label : '';

        const labelContent = (
          <Flex justify="space-between" align="center" style={{ width: '100%', overflow: 'hidden' }}>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </span>
            {isLocked && (
              <span style={{ fontSize: 11, color: '#f59e0b', marginLeft: 4, flexShrink: 0 }}>
                🔒
              </span>
            )}
          </Flex>
        );

        if (textLabel && !collapsed) {
          return (
            <Tooltip title={textLabel} placement="right" mouseEnterDelay={0.4}>
              {labelContent}
            </Tooltip>
          );
        }

        return labelContent;
      };

      const rolesList = userRolesList && userRolesList.length > 0
        ? userRolesList
        : (userRole && userRole !== 'unknown' ? [userRole] : []);

      // Only restrict if ALL of their roles are restricted (possessing at least one unrestricted role grants full layout)
      const restricted = rolesList.length > 0
        ? rolesList.every((role: string) => isRestrictedRole(role))
        : isRestrictedRole(userRole);

      if (restricted) {
        // Flatten for restricted roles like Waiters/Chefs to avoid extra clicks
        const flatList: AntMenuItem[] = [];
        const flatten = (itemList: MenuItem[]) => {
          itemList.forEach((item) => {
            if (item.children && item.children.length > 0) {
              flatten(item.children);
            } else {
              flatList.push({
                key: item.path || item.key,
                icon: item.icon,
                label: getLabel(item),
                title: typeof item.label === 'string' ? item.label : undefined,
              });
            }
          });
        };
        flatten(items);
        return flatList;
      }

      return items.map((item) => {
        const itemKey = item.key || item.path || '';
        if (item.children && item.children.length > 0) {
          return {
            key: itemKey,
            icon: item.icon,
            label: getLabel(item),
            title: typeof item.label === 'string' ? item.label : undefined,
            children: item.children.map((child) => ({
              key: child.key || child.path || '',
              icon: child.icon,
              label: getLabel(child),
              title: typeof child.label === 'string' ? child.label : undefined,
              children: child.children
                ? child.children.map((subChild) => ({
                    key: subChild.key || subChild.path || '',
                    icon: subChild.icon,
                    label: getLabel(subChild),
                    title: typeof subChild.label === 'string' ? subChild.label : undefined,
                  }))
                : undefined,
            })),
          };
        }
        return {
          key: itemKey,
          icon: item.icon,
          label: getLabel(item),
          title: typeof item.label === 'string' ? item.label : undefined,
        };
      });
    },
    [userRole, userRolesList, hasFeature, collapsed]
  );

  // Permission filter — also applies role-based feature restriction
  const filterItemsByPermissions = useCallback(
    (items: MenuItem[]): MenuItem[] => {
      return items
        .filter((item) => {
          if (item.key?.startsWith('shoe_') || businessType === 'shoe') return true;
          if (item.key?.startsWith('jewelry_') || businessType === 'jewelry') return true;
          if (item.key?.startsWith('hotel_') || businessType === 'hotel' || (businessType as string) === 'hotel & hospitality' || (businessType as string).includes('hotel') || (businessType as string).includes('hospitality')) return true;
          if (!hasAccess(item.key)) return false;
          if (!item.featureKey) return true;

          // Check the actual computed permission (including role templates & user overrides)
          return hasPermission(item.featureKey);
        })
        .map((item) => {
          if (item.children) {
            return {
              ...item,
              children: filterItemsByPermissions(item.children),
            };
          }
          return item;
        })
        .filter((item) => !item.children || item.children.length > 0);
    },
    [hasAccess, hasPermission, businessType]
  );

  // Map key -> path for navigation
  const keyToPathMap = useMemo(() => {
    const map: Record<string, string> = {};
    const traverse = (items: MenuItem[]) => {
      items.forEach((item) => {
        if (item.key && item.path) map[item.key] = item.path;
        if (item.children) traverse(item.children);
      });
    };
    menuItems.forEach((section: any) => {
      if (section.children) traverse(section.children);
    });
    return map;
  }, [menuItems]);

  // Build final Ant Design items
  const antdMenuItems = useMemo((): AntMenuItem[] => {
    const result: AntMenuItem[] = [];

    menuItems.forEach((section: any) => {
      const filteredChildren = filterItemsByPermissions(section.children);

      if (section.type === 'group' && filteredChildren.length > 0) {
        if (collapsed) {
          result.push(...convertToAntdItems(filteredChildren));
        } else {
          result.push({
            type: 'group',
            label: section.label,
            children: convertToAntdItems(filteredChildren),
          });
        }
      }
    });

    return result;
  }, [menuItems, collapsed, convertToAntdItems, filterItemsByPermissions]);

  // Find selected key based on current path (prioritizes exact match over prefix)
  const selectedKeys: string[] = useMemo(() => {
    const currentPath = location.pathname;
    let exactMatch: string | null = null;
    let prefixMatch: string | null = null;

    const traverse = (items: MenuItem[]) => {
      items.forEach((item: MenuItem) => {
        if (item.path) {
          if (item.path === currentPath) {
            exactMatch = item.key || item.path;
          } else if (item.path !== '/' && currentPath.startsWith(item.path + '/') && !prefixMatch) {
            prefixMatch = item.key || item.path;
          }
        }
        if (item.children) {
          traverse(item.children);
        }
      });
    };

    menuItems.forEach((section: any) => {
      if (section.children) {
        traverse(section.children);
      }
    });

    const activeKey = exactMatch || prefixMatch;
    return activeKey ? [activeKey] : [];
  }, [location.pathname, menuItems]);

  // Auto-expand parent of active item
  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
      return;
    }

    const parentKeys: string[] = [];

    menuItems.forEach((section: any) => {
      section.children.forEach((item: MenuItem) => {
        if (item.children) {
          const hasActiveChild = item.children.some((child: MenuItem) =>
            selectedKeys.includes(child.key || child.path || '')
          );
          if (hasActiveChild) {
            parentKeys.push(item.key);
          }
        }
      });
    });

    if (parentKeys.length > 0) {
      setOpenKeys((prev) => {
        const newKeys = [...prev];
        parentKeys.forEach((key) => {
          if (!newKeys.includes(key)) newKeys.push(key);
        });
        return newKeys;
      });
    }
  }, [selectedKeys, menuItems, collapsed]);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const handleMenuSelect: MenuProps['onSelect'] = useCallback(
    ({ key }: { key: string }) => {
      const featureCode = KEY_TO_FEATURE_CODE[key];
      if (featureCode && !hasFeature(featureCode as any)) {
        Modal.warning({
          title: translate('PREMIUM FEATURE LOCKED'),
          content: translate('This module is not included in your current subscription plan. Please upgrade your pricing plan to unlock this capability.'),
          okText: translate('Upgrade Plan'),
          closable: true,
          onOk: () => {
            navigate('/settings');
          }
        });
        return;
      }

      const targetPath = keyToPathMap[key] || (key.startsWith('/') ? key : undefined);
      if (targetPath) {
        navigate(targetPath);
      }
    },
    [navigate, hasFeature, translate, keyToPathMap]
  );

  return (
    <Menu
      mode="inline"
      theme="dark"
      selectedKeys={selectedKeys}
      openKeys={openKeys}
      onOpenChange={handleOpenChange}
      onSelect={handleMenuSelect}
      items={antdMenuItems}
      inlineCollapsed={collapsed}
      style={{
        background: 'transparent',
        borderRight: 'none',
        marginTop: 8,
      }}
    />
  );
};

export default MenuItems;